import React, {
  useEffect,
  useState,
} from "react";

import "../css/ferrypass.css";

import {
  RefreshCcw,
  ShipWheel,
  CalendarDays,
  Route,
  BadgeDollarSign,
    CarFront,
  Search,
  Trash2,
  X,
  TriangleAlert
} from "lucide-react";

import { printReceipt } from "../utils/printReceipt";

/* =====================================================
    API
===================================================== */

const RAW_API_BASE =
  (
    typeof import.meta !==
    "undefined"
      ? import.meta.env
          ?.VITE_API_BASE
      : undefined
  ) ||
  process.env
    .REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE =
  RAW_API_BASE.replace(
    /\/+$/,
    ""
  ).replace(
    /\/api$/i,
    ""
  );

const api = (path) =>
  `${API_BASE}${
    path.startsWith("/api")
      ? path
      : `/api${path}`
  }`;

/* =====================================================
    AUTH
===================================================== */

const safeJson = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const getAuth = () => {

  const raw =
    sessionStorage.getItem(
      "auth"
    ) ||
    localStorage.getItem(
      "auth"
    ) ||
    sessionStorage.getItem(
      "rcs_auth"
    ) ||
    localStorage.getItem(
      "rcs_auth"
    );

  const obj =
    raw && safeJson(raw);

  if (
    obj &&
    (
      obj.access_token ||
      obj.token
    )
  ) {

    return {
      access_token:
        obj.access_token ||
        obj.token,
      user:
        obj.user ||
        obj.profile ||
        null,
    };

  }

  return {};

};

const authHeader = () => {

  const {
    access_token,
  } = getAuth();

  return access_token
    ? {
        Authorization: `Bearer ${access_token}`,
      }
    : {};

};


const formatDate = (value) => {
  if (!value) return "-";

  const [year, month, day] = value.split("-");

  return `${month}/${day}/${year}`;
};

/* =====================================================
    COMPONENT
===================================================== */

export default function Ferrypass() {

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [err, setErr] =
    useState("");

  const [selectedDate, setSelectedDate] =
    useState(
      new Date()
        .toISOString()
        .split("T")[0]
    );

    const [showTravelers, setShowTravelers] = useState(false);

const [travelers, setTravelers] = useState([]);

const [travelerSearch, setTravelerSearch] = useState("");

const [loadingTravelers, setLoadingTravelers] = useState(false);

const [
    travelerToDelete,
    setTravelerToDelete
] = useState(null);

const [
    deletingTraveler,
    setDeletingTraveler
] = useState(false);


  /* =====================================================
      FETCH
  ===================================================== */

  const fetchFerry =
    async () => {

      setLoading(true);

      setErr("");

      try {

        const url =
          api(
            `/sales/ferry?limit=5000&date=${selectedDate}`
          );

        const res =
          await fetch(
            url,
            {
              headers: {
                "Content-Type":
                  "application/json",
                ...authHeader(),
              },
            }
          );

        if (!res.ok) {

          const text =
            await res.text();

          throw new Error(
            text ||
              `HTTP ${res.status}`
          );

        }

        const data =
          await res.json();

        const normalized =
          (
            Array.isArray(data)
              ? data
              : []
          ).map((s) => {

            let ferry =
              s.ferry_info;

            if (
              typeof ferry ===
                "string" &&
              ferry.trim()
            ) {

              try {

                ferry =
                  JSON.parse(
                    ferry
                  );

              } catch {}

            }

            if (
              ferry &&
              typeof ferry ===
                "object" &&
              !Array.isArray(
                ferry
              )
            ) {

              return {
                ...s,
                ferry_info:
                  ferry,
              };

            }

            return {
              ...s,
              ferry_info:
                {},
            };

          });

        setRows(normalized);

      } catch (e) {

        console.error(
          "Failed to load ferry bookings:",
          e
        );

        setErr(
          String(
            e.message || e
          )
        );

        setRows([]);

      } finally {

        setLoading(false);

      }

    };

  useEffect(() => {

    fetchFerry();

  }, [selectedDate]);



  const fetchTravelers = async () => {

    setLoadingTravelers(true);

    try{

        const res = await fetch(

            api("/ferry/frequent-travelers"),

            {

                headers:{

                    ...authHeader()

                }

            }

        );

        if(!res.ok)
            throw new Error();

        const data = await res.json();

        setTravelers(data);

    }

    catch(err){

        console.error(err);

    }

    finally{

        setLoadingTravelers(false);

    }

};


const deleteTraveler = async () => {

    if (!travelerToDelete)
        return;

    try {

        setDeletingTraveler(true);

        const res = await fetch(

            api(`/ferry/frequent-travelers/${travelerToDelete.id}`),

            {

                method:"DELETE",

                headers:{
                    ...authHeader()
                }

            }

        );

        if(!res.ok)
            throw new Error();

        setTravelers(prev=>

            prev.filter(

                t=>t.id!==travelerToDelete.id

            )

        );

        setTravelerToDelete(null);

    }

    catch(err){

        console.error(err);

    }

    finally{

        setDeletingTraveler(false);

    }

};

  /* =====================================================
      TOTALS
  ===================================================== */

  const totalRevenue =
    rows.reduce(
      (sum, r) =>
        sum +
        (
          parseFloat(
            r.total
          ) || 0
        ),
      0
    );

  const roundTrips =
    rows.filter(
      (r) =>
        r.ferry_info
          ?.round_trip
    ).length;

  const routes =
    [
      ...new Set(
        rows.map(
          (r) =>
            r.ferry_info
              ?.route
        )
      ),
    ].filter(Boolean)
      .length;



const filteredTravelers = travelers.filter((t) => {

    const q = travelerSearch
        .trim()
        .toLowerCase();

    if (!q) return true;

    return (

        t.customer_name
            ?.toLowerCase()
            .includes(q)

        ||

        t.vehicle_number
            ?.toLowerCase()
            .includes(q)

    );

});



  /* =====================================================
      RETURN
  ===================================================== */

  return (

    <div className="modern-ferry-page">

      {/* =====================================================
          HERO
      ===================================================== */}
      <div className="modern-ferry-hero">

        <div className="modern-ferry-hero-left">

          <div className="modern-ferry-pill">
            Ferry Operations
          </div>

          <h1>
            FerryPass
          </h1>

          <p>
            Monitor ferry pass
            bookings, routes,
            vehicle traffic and
            operational ferry
            activity across the
            RCS ecosystem.
          </p>

        </div>

        {/* =====================================================
            ACTIONS
        ===================================================== */}
        <div className="modern-ferry-actions">

          {/* REFRESH */}
          <button
            className="modern-ferry-action modern-ferry-action-teal"
            onClick={
              fetchFerry
            }
          >

            <div className="modern-ferry-action-icon">
              <RefreshCcw size={18} />
            </div>

            <div className="modern-ferry-action-content">

              <span>
                Refresh
              </span>

              <p>
                Reload bookings
              </p>

            </div>

          </button>

          {/* BOOKINGS */}
          <button
            className="modern-ferry-action modern-ferry-action-blue"
          >

            <div className="modern-ferry-action-icon">
              <ShipWheel size={18} />
            </div>

            <div className="modern-ferry-action-content">

              <span>
                Bookings
              </span>

              <p>
                {rows.length}
                {" "}
                loaded
              </p>

            </div>

          </button>

<button
    className="modern-ferry-action modern-ferry-action-purple"
    onClick={async()=>{

        await fetchTravelers();

        setShowTravelers(true);

    }}
>

    <div className="modern-ferry-action-icon">

        <CarFront size={18}/>

    </div>

    <div className="modern-ferry-action-content">

        <span>

            Frequent Travelers

        </span>

        <p>

            {travelers.length} Saved

        </p>

    </div>

</button>

        </div>

      </div>

      {/* =====================================================
          TOOLBAR
      ===================================================== */}
      <div className="modern-ferry-toolbar">

        <div className="modern-ferry-date-wrap">

          <CalendarDays size={18} />

          <input
            type="date"
            value={selectedDate}
            onChange={(e) =>
              setSelectedDate(
                e.target.value
              )
            }
            className="modern-ferry-date-input"
          />

        </div>

      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}
      {!loading && (

        <div className="modern-ferry-summary-grid">

          {/* BOOKINGS */}
          <div className="modern-ferry-summary-card">

            <span>
              Total Bookings
            </span>

            <h2>
              {rows.length}
            </h2>

          </div>

          {/* REVENUE */}
          <div className="modern-ferry-summary-card">

            <span>
              Revenue
            </span>

            <h2 className="green">

              $
              {totalRevenue.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}

            </h2>

          </div>

          {/* ROUND */}
          <div className="modern-ferry-summary-card">

            <span>
              Round Trips
            </span>

            <h2 className="blue">
              {roundTrips}
            </h2>

          </div>

          {/* ROUTES */}
          <div className="modern-ferry-summary-card">

            <span>
              Active Routes
            </span>

            <h2 className="purple">
              {routes}
            </h2>

          </div>

        </div>

      )}

      {/* =====================================================
          TABLE CARD
      ===================================================== */}
      <div className="modern-ferry-table-card">

        <div className="modern-ferry-card-header">

          <div>

            <h3>
              Ferry Bookings
            </h3>

            <span>
              Operational ferry
              activity and travel
              records
            </span>

          </div>

        </div>

        {/* ERROR */}
        {err && (

          <div className="modern-ferry-error">

            Failed to load ferry
            bookings ({err})

          </div>

        )}

        {/* =====================================================
            TABLE
        ===================================================== */}
        <div className="modern-ferry-table-wrap">

          <table className="modern-ferry-table">

            <thead>

              <tr>

                <th>
                  Receipt
                </th>

                <th>
                  Customer
                </th>

                <th>
                  Vehicle
                </th>

                <th>
                  Type
                </th>

                <th>
                  Route
                </th>

                <th>
                  Travel
                </th>

                <th>
                  Return
                </th>

                <th>
                  Booking
                </th>

                <th>
                  Payment
                </th>

                <th>
                  Amount
                </th>

                <th>
                  Date
                </th>

              </tr>

            </thead>

            <tbody>

              {loading ? (

                <tr>

                  <td
                    colSpan={11}
                    className="modern-ferry-loading"
                  >

                    Loading ferry
                    bookings...

                  </td>

                </tr>

              ) : rows.length === 0 ? (

                <tr>

                  <td
                    colSpan={11}
                    className="modern-ferry-loading"
                  >

                    No ferry pass
                    bookings found.

                  </td>

                </tr>

              ) : (

                rows.map((sale) => {

                  const ferry =
                    sale.ferry_info || {};

                  const rec =
                    sale.payment_method ===
                    "Cash"
                      ? `RCSCS${sale.receipt_no?.replace(
                          "RCS",
                          ""
                        )}`
                      : `RCSCR${sale.receipt_no?.replace(
                          "RCS",
                          ""
                        )}`;

                  return (

                    <tr key={sale.id}>

                      {/* RECEIPT */}
                      <td>

                        <button
                          className="modern-ferry-receipt-btn"
                          onClick={() =>
                            printReceipt(
                              sale
                            )
                          }
                        >

                          {rec}

                        </button>

                      </td>

                      {/* CUSTOMER */}
                      <td className="modern-ferry-customer">

                        {sale.customer_name ||
                          "Unknown"}

                      </td>

                      {/* VEHICLE */}
                      <td>

                        {ferry.vehicle_no ||
                          ferry.vehicle ||
                          "-"}

                      </td>

                      {/* TYPE */}
                      <td>

                        {ferry.vehicle_type ||
                          "-"}

                      </td>

                      {/* ROUTE */}
                      <td className="modern-ferry-route">

                        {ferry.route
                          ? ferry.route.replace(
                              "-",
                              " → "
                            )
                          : "-"}

                      </td>


{/* TRAVEL */}
<td>
  {formatDate(ferry.travel_date)}
</td>

{/* RETURN */}
<td>
  {ferry.round_trip
    ? ferry.return_date
      ? formatDate(ferry.return_date)
      : "Yes"
    : "No"}
</td>

                      {/* BOOKING */}
                      <td>

                        {ferry.booking ||
                          "-"}

                      </td>

                      {/* PAYMENT */}
                      <td>

                        {ferry.payment ||
                          "-"}

                      </td>

                      {/* AMOUNT */}
                      <td className="modern-ferry-amount">

                        $
                        {Number(
                          sale.total || 0
                        ).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                          }
                        )}

                      </td>

                      {/* DATE */}
                      <td>

                        {new Date(
                          sale.created_at
                        ).toLocaleDateString(
                          "en-US"
                        )}

                      </td>

                    </tr>

                  );
                })

              )}

            </tbody>

          </table>

        </div>

      </div>



{showTravelers && (

<div className="modern-travelers-overlay">

    <div className="modern-travelers-modal">

{/* Header */}

<div className="modern-travelers-header">

    <div className="modern-travelers-title">

        <div className="modern-travelers-pill">

            <CarFront size={14}/>

            Frequent Travelers

        </div>

        <h2>

            Frequent Travelers

        </h2>

        <p>

            Browse returning customers and instantly retrieve their saved vehicle information.

        </p>

    </div>

    <button

        className="modern-travelers-close"

        onClick={() =>

            setShowTravelers(false)

        }

    >

        <X size={20}/>

    </button>

</div>

        {/* Search */}

        <div className="modern-travelers-search">

            <Search size={18}/>

            <input

                placeholder="Search customer or vehicle..."

                value={travelerSearch}

                onChange={(e)=>
                    setTravelerSearch(e.target.value)
                }

            />

        </div>

        {/* Table */}

        <div className="modern-travelers-table-wrap">

            <table className="modern-travelers-table">

                <thead>

                    <tr>

                        <th>

                            Customer

                        </th>

                        <th>

                            Vehicle

                        </th>

                        <th>

                            Type

                        </th>

                        <th>

                            Action

                        </th>

                    </tr>

                </thead>

                <tbody>

                    {loadingTravelers ? (

<tr>

    <td
        colSpan={4}
        className="modern-travelers-empty"
    >

        <div className="modern-travelers-empty-box">

            <div className="modern-travelers-empty-icon loading">

                <CarFront size={34}/>

            </div>

            <h3>

                Loading Travelers...

            </h3>

            <p>

                Please wait while we retrieve your saved travelers.

            </p>

        </div>

    </td>

</tr>

                    ) : filteredTravelers.length===0 ? (

<tr>

    <td
        colSpan={4}
        className="modern-travelers-empty"
    >

        <div className="modern-travelers-empty-box">

            <div className="modern-travelers-empty-icon">

                <TriangleAlert size={38}/>

            </div>

            <h3>

                Oh no!

            </h3>

            <p>

                It looks like there are no frequent traveler records yet.

            </p>

            <span>

                Travelers will automatically appear here after their first completed ferry booking.

            </span>

        </div>

    </td>

</tr>

                    ) : (

                        filteredTravelers.map((traveler)=>(

                            <tr
                                key={traveler.id}
                            >

                                <td>

                                    {traveler.customer_name}

                                </td>

                                <td>

                                    {traveler.vehicle_number}

                                </td>

                                <td>

                                    {traveler.vehicle_type}

                                </td>

                                <td>

<button

    className="modern-traveler-delete"

onClick={() =>
    setTravelerToDelete(traveler)
}

>

    <Trash2
        size={16}
    />

</button>
                                </td>

                            </tr>

                        ))

                    )}

                </tbody>

            </table>

        </div>

    </div>

</div>

)}


{travelerToDelete && (

<div className="transaction-status-overlay">

    <div className="modern-delete-modal">

        <div className="modern-delete-icon">

            <TriangleAlert size={46}/>

        </div>

        <h2>

            Delete Frequent Traveler?

        </h2>

        <p>

            This will permanently remove the saved vehicle from the customer's
            frequent traveler history.

        </p>

        <div className="modern-delete-preview">

            <strong>

                {travelerToDelete.customer_name}

            </strong>

            <span>

                {travelerToDelete.vehicle_number}

            </span>

            <small>

                {travelerToDelete.vehicle_type}

            </small>

        </div>

        <div className="modern-delete-actions">

            <button

                className="modern-delete-cancel"

                onClick={()=>setTravelerToDelete(null)}

                disabled={deletingTraveler}

            >

                Cancel

            </button>

            <button

                className="modern-delete-confirm"

                onClick={deleteTraveler}

                disabled={deletingTraveler}

            >

                {deletingTraveler
                    ? "Deleting..."
                    : "Delete Traveler"}

            </button>

        </div>

    </div>

</div>

)}

    </div>

  );

}